import LocationCommonWellItem from './LocationCommonWellItem';
import getLocationPickerProps from '../utils/getLocationPickerProps';
import LocationCommonWellItemViewState from '../store/schema/LocationCommonWellItemViewState';
import LocationPickerState, { SearchContext } from '../store/schema/LocationPickerState';
import { wrapWithLocationCard } from 'owa-location-card-v2';
import RequestStatus from '../store/schema/RequestStatus';
import { ITheme } from '@uifabric/styling';
import { observer } from 'mobx-react';
import { IInputProps } from 'office-ui-fabric-react/lib/Pickers';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';

import requestCurrentPosition from '../utils/requestCurrentPosition';
import TimeSlot from 'owa-service/lib/contract/TimeSlot';
import * as React from 'react';
import { IPeopleFloatingPickerProps } from 'office-ui-fabric-react/lib/FloatingPicker';
import {
    ReadWriteCommonWell,
    ReadWriteCommonWellProps,
    ReadWriteCommonWellState,
} from 'owa-readwrite-common-well/lib/components/ReadWriteCommonWell';
import {
    ISelectedPeopleProps,
    ISelectedPeopleItemProps,
} from 'office-ui-fabric-react/lib/SelectedItemsList';
import { customizable } from 'office-ui-fabric-react/lib/Utilities';

import SearchMode from '../store/schema/SearchMode';

export interface LocationCommonWellProps
    extends ReadWriteCommonWellProps<
        LocationCommonWellItemViewState,
        LocationCommonWellItemViewState,
        LocationCommonWellItemViewState
    > {
    inputProps: IInputProps;
    onLocationsChanged: (items: LocationCommonWellItemViewState[]) => void;
    roomFreeBusyEnabled?: boolean;
    event?: CalendarEvent;
    timeSlot?: TimeSlot;
    theme?: ITheme;
    colorScheme?: ColorScheme;
    itemLimit?: number;
    isSmallSize?: boolean;
    maxItemWidth?: number;
    zeroQueryDisabled?: boolean;
    searchMode?: SearchMode;
    disableUseThisLocationButton?: boolean;
    wellClassName?: string;
}

export interface LocationCommonWellState
    extends ReadWriteCommonWellState<LocationCommonWellItemViewState>,
        LocationPickerState {}

const styles = require('./LocationCommonWell.scss');

@customizable('LocationCommonWell', ['theme'])
@observer
export default class LocationCommonWell extends ReadWriteCommonWell<
    LocationCommonWellProps,
    LocationCommonWellItemViewState, // suggestion type
    LocationCommonWellItemViewState, // resolved item type
    LocationCommonWellItemViewState, // external (controlled component) selectedItems
    LocationCommonWellState
> {
    constructor(props: LocationCommonWellProps) {
        super(props);
        this.state = {
            items: [], // resolved item set
            // Picker state
            findLocationRequestStatus: RequestStatus.NotInitiated,
            displayedFindLocationRequestTimestamp: null,
            queryString: '',
            suggestionSet: [], // suggestion set
        };
    }

    protected getFindControlProps = (): Partial<IPeopleFloatingPickerProps> => {
        return getLocationPickerProps(
            this.getPickerState,
            this.onPickerStateChanged,
            this.getSearchContext,
            this.props.theme,
            this.props.colorScheme,
            this.props.roomFreeBusyEnabled,
            this.props.disableUseThisLocationButton,
            this.props.zeroQueryDisabled
        );
    };

    // Extra props for the selected item collection. Not used for the location well
    protected getExtendedSelectedItemsListProps = (): ISelectedPeopleProps => {
        return null;
    };

    // Extra props for the well control
    protected extendedPickerProps(): any {
        return {
            inputProps: {
                ...this.props.inputProps,
                onFocus: this.onInputFocus,
            },
            itemLimit: this.props.itemLimit,
            className: styles.input,
        };
    }

    protected renderSelectedItem = (props: ISelectedPeopleItemProps): JSX.Element => {
        let { theme, isSmallSize, maxItemWidth } = this.props;
        let viewState = props.item as LocationCommonWellItemViewState;
        let location = (
            <LocationCommonWellItem
                key={`${viewState.persona.personaId}_${props.index}`}
                index={props.index}
                viewState={viewState}
                removeOperation={this.removeLocation}
                useContextMenu={true}
                isSelected={props.selected}
                theme={theme}
                isSmallSize={isSmallSize}
                maxItemWidth={maxItemWidth}
            />
        );

        return wrapWithLocationCard(
            viewState.persona.displayName,
            viewState.persona.postalAddress,
            viewState.persona.personaId,
            location,
            this.props.colorScheme,
            null /* onLocationCardUnmounted */
        );
    };

    protected convertSuggestionItemToWellItem = (
        suggestionItem: LocationCommonWellItemViewState
    ): LocationCommonWellItemViewState => {
        return suggestionItem;
    };

    protected getDefaultSelectedItems = (): LocationCommonWellItemViewState[] => {
        const { event, defaultSelectedItems } = this.props;

        if (defaultSelectedItems) {
            return defaultSelectedItems;
        }

        return event
            ? event.Locations.map(location => {
                  return {
                      persona: {
                          personaId: location.Id,
                          displayName: location.DisplayName,
                          postalAddress: location.PostalAddress,
                      },
                      text: location.DisplayName,
                      isValid: true,
                      isSelected: false,
                      isContextMenuOpen: false,
                  } as LocationCommonWellItemViewState;
              })
            : [];
    };

    protected convertInitialItemsToWellItems = (
        items: LocationCommonWellItemViewState[]
    ): LocationCommonWellItemViewState[] => {
        return items;
    };

    protected onSelectedItemsChanged = (items: LocationCommonWellItemViewState[]): void => {
        this.props.onLocationsChanged(items);
    };

    protected onPaste = async (pastedText: string): Promise<LocationCommonWellItemViewState[]> => {
        this.picker.inputElement.value = pastedText;
        this.picker.floatingPicker.current.onQueryStringChanged(pastedText);
        return null;
    };

    protected async parsePastedTextForItems(
        pastedText: string
    ): Promise<LocationCommonWellItemViewState[]> {
        return null;
    }

    protected onKeyDown = async (ev: React.KeyboardEvent<EventTarget>) => {
        return null;
    };

    protected getItemIndex(item: LocationCommonWellItemViewState) {
        //This is implementing the abstract function in super class.
        //This is used for drag and drop. Since drag and drop does
        //not happen in location well, we return -1 as a generic value
        return -1;
    }

    /**
     * Call back to get the location dropdow (state). It is used to generate the props for the location picker common control
     */
    private getPickerState = (): LocationPickerState => {
        return this.state;
    };

    /**
     * Call back to handle the location picker state change.
     */
    private onPickerStateChanged = (state: Partial<LocationPickerState>): void => {
        this.setState(state as LocationCommonWellState);
    };

    private getSearchContext = (): SearchContext => {
        return {
            event: this.props.event,
            timeSlot: this.props.timeSlot,
            searchMode: this.props.searchMode,
            currentDevicePosition: this.currentDevicePosition,
        };
    };

    private onInputFocus = (): void => {
        requestCurrentPosition(position => {
            this.currentDevicePosition = position;
        });
    };

    private currentDevicePosition: Position;

    private removeLocation = (location: LocationCommonWellItemViewState) => {
        this.picker.selectedItemsList.current.removeItems([location]);
    };
}
